export function getClusterIds(timeseries: any[]): number[] {
    return (timeseries[0]?.data?.clusters ?? []).map(
        (c: { cluster_id: number }) => c.cluster_id
    );
}

export interface ClusterReturnSeries {
    asset: string;
    firstPrice: number;
    timeseriesData: Array<{
        timestamp: string;
        price: number;
        activeClusterId: number | undefined;
        clusterReturns: Record<number, number>;
        clusterFeatures: Record<number, Record<string, number>>;
        clusterMetrics: Record<number, Record<string, number>>;
    }>;
}

export interface StrategyReturnSeries {
    firstPrices: Record<string, number>;
    timeseriesData: Array<{
        timestamp: string;
        prices: Record<string, number>;
        strategyReturn: number;
        targetDistribution: Record<string, number>;
    }>;
}

export function computeStrategyReturns(
    timeseries: any[],
    strategy: string
): StrategyReturnSeries {
    const timeseriesData: StrategyReturnSeries["timeseriesData"] = [];
    let asset = "";
    let firstPrices: Record<string, number> = {};
    let strategyReturn: number = 0;

    for (let i = 0; i < timeseries.length; i++) {
        const { timestamp, data } = timeseries[i];

        const prices: Record<string, number> = Object.fromEntries(
            Object.entries(data?.prices || {}).map(([symbol, price]) =>
                [symbol, parseFloat(<string>price)]
            )
        );

        if (i === 0) {
            firstPrices = prices;
        }

        if (i > 0) {
            const prevData = timeseries[i - 1]?.data;
            const pricesPrev = Object.fromEntries(
                Object.entries(prevData?.prices || {}).map(([symbol, price]) =>
                    [symbol, parseFloat(<string>price)]
                )
            );
            const pricesCurr = prices;

            strategyReturn = 0;
            const targetDistribution = prevData?.target_distribution || {};
            Object.keys(targetDistribution).forEach((key) => {
                const weight = parseFloat(targetDistribution[key] || 0);
                if (weight > 0 && pricesPrev[key] && pricesCurr[key]) {
                    const assetReturn = (pricesCurr[key] / pricesPrev[key]) - 1;
                    strategyReturn += weight * assetReturn;
                }
            });
        }

        const targetDist = i > 0 ? timeseries[i - 1]?.data?.target_distribution || {} : {};
        const targetDistribution: Record<string, number> = {};
        
        // Filter and parse only non-zero allocations
        let totalCryptoAllocation = 0;
        Object.entries(targetDist).forEach(([asset, weight]) => {
            const allocation = parseFloat(weight as string);
            if (allocation > 0.001) {
                targetDistribution[asset] = allocation;
                totalCryptoAllocation += allocation;
            }
        });
        
        // Add USD allocation if portfolio is partially or fully in cash
        const usdAllocation = 1 - totalCryptoAllocation;
        if (usdAllocation > 0.001) {
            targetDistribution['USD'] = usdAllocation;
        }

        timeseriesData.push({
            timestamp,
            prices,
            strategyReturn,
            targetDistribution
        });
    }


    return {
        firstPrices,
        timeseriesData
    };
}

export function computeBenchmarkReturns(
    timeseries: any[]
): StrategyReturnSeries {
    const timeseriesData: StrategyReturnSeries["timeseriesData"] = [];
    let firstPrices: Record<string, number> = {};
    let benchmarkReturn = 0;

    for (let i = 0; i < timeseries.length; i++) {
        const { timestamp, data } = timeseries[i];
        const base = data?.base || "UNKNOWN";
        const close = parseFloat(<string>data?.close ?? "0");

        // mimic `prices` structure like in computeStrategyReturns
        const prices: Record<string, number> = { [base]: close };

        if (i === 0) {
            firstPrices = prices;
        }

        if (i > 0) {
            const prevData = timeseries[i - 1]?.data;
            const prevBase = prevData?.base || "UNKNOWN";
            const prevClose = parseFloat(<string>prevData?.close ?? "0");

            const pricesPrev: Record<string, number> = { [prevBase]: prevClose };
            const pricesCurr: Record<string, number> = prices;

            benchmarkReturn = 0;
            if (prevClose > 0 && close > 0) {
                benchmarkReturn = (close / prevClose) - 1;
            }
        }

        timeseriesData.push({
            timestamp,
            prices,
            strategyReturn: benchmarkReturn,
            targetDistribution: {}  // Benchmarks don't have asset distributions
        });
    }

    return {
        firstPrices,
        timeseriesData
    };
}



// Updated computeCumulativeReturns function
export const computeCumulativeReturns = (
    timeseriesData: StrategyReturnSeries["timeseriesData"],
    turnoverCost: number = 0
): { combined: number[] } => {
    let combinedCumulative = 1.0;
    const combinedReturns: number[] = [];
    // NEW
    const EPS = 1e-6;

    timeseriesData.forEach((data, i) => {

        // build multiplier
        let multiplier = 1 + data.strategyReturn;
        // if (turnoverCost > 0) {
        //     multiplier *= Math.max(0, 1 - turnoverCost * delta);
        // }
        //TODO: include turnover cost

        combinedCumulative *= multiplier;
        combinedCumulative = Math.max(0, combinedCumulative);
        combinedReturns.push(combinedCumulative);
    });

    return {
        combined: combinedReturns
    };
};

export function computeDomainsFromCumulativeReturns(
    prices: Record<string, number>[],
    combinedReturns: number[],
    firstPrices: Record<string, number>
): {
    priceDomain: [number, number];
    returnDomain: [number, number];
    logPriceDomain: [number, number];
    logReturnDomain: [number, number];
} {
    const allPriceValues = prices.flatMap(priceObj => Object.values(priceObj));
    const priceOrigin = Object.values(firstPrices)[0] || 1;
    const returnOrigin = 1.0;

    const allReturnValues = combinedReturns;

    const minPrice = Math.min(...allPriceValues);
    const maxPrice = Math.max(...allPriceValues);
    const minReturn = Math.min(...allReturnValues);
    const maxReturn = Math.max(...allReturnValues);

    // LINEAR SCALE DOMAINS
    // Enforce minimum of 0 for returns (representing -100% loss)
    const safeMinReturn = Math.max(0, minReturn);

    const priceCenter = (minPrice + maxPrice) / 2;
    const returnCenter = (safeMinReturn + maxReturn) / 2;

    const priceShift = (priceCenter - priceOrigin) / priceOrigin;
    const returnShift = (returnCenter - returnOrigin) / returnOrigin;
    const avgShift = (priceShift + returnShift) / 2;

    const priceRange = Math.max(
        Math.abs(minPrice - priceOrigin),
        Math.abs(maxPrice - priceOrigin)
    ) / priceOrigin;
    const returnRange = Math.max(
        Math.abs(safeMinReturn - returnOrigin),
        Math.abs(maxReturn - returnOrigin)
    ) / returnOrigin;

    const maxRange = Math.max(priceRange, returnRange);
    const paddedRange = maxRange * 1.05;

    const shiftedPriceCenter = priceOrigin * (1 + avgShift);
    const shiftedReturnCenter = returnOrigin * (1 + avgShift);

    const priceLower = Math.max(0, shiftedPriceCenter - (priceOrigin * paddedRange));
    const returnLower = Math.max(0, shiftedReturnCenter - (returnOrigin * paddedRange));

    // LOG SCALE DOMAINS
    // For returns, minimum is 0.01 (representing -99% loss) to avoid log(0)
    const logMinReturn = Math.max(0.01, safeMinReturn);
    const safeMinPrice = Math.max(0.001, minPrice);

    // Calculate ratios from origin
    const priceRatioDown = priceOrigin / safeMinPrice;
    const priceRatioUp = maxPrice / priceOrigin;
    const returnRatioDown = returnOrigin / logMinReturn;
    const returnRatioUp = maxReturn / returnOrigin;

    // Use tighter bounds for log scale
    const maxDownRatio = Math.max(priceRatioDown, returnRatioDown);
    const maxUpRatio = Math.max(priceRatioUp, returnRatioUp);

    const paddedDownRatio = maxDownRatio * 1.05;
    const paddedUpRatio = maxUpRatio * 1.05;

    return {
        priceDomain: [
            priceLower,
            shiftedPriceCenter + (priceOrigin * paddedRange)
        ],
        returnDomain: [
            returnLower,
            shiftedReturnCenter + (returnOrigin * paddedRange)
        ],
        logPriceDomain: [
            Math.max(0.001, priceOrigin / paddedDownRatio),
            priceOrigin * paddedUpRatio
        ],
        logReturnDomain: [
            Math.max(0.01, returnOrigin / paddedDownRatio),
            returnOrigin * paddedUpRatio
        ]
    };
}

export type StrategyMetricMap = Record<string, number>;

export function calculateStrategyMetrics(returns: number[], riskFreeRate = 0, activeFlags?: boolean[]): StrategyMetricMap {
    const safe = (x: number) => (isFinite(x) ? x : 0);

    // Filter returns to only include active trading days if flags provided
    const activeReturns = activeFlags
        ? returns.filter((_, i) => activeFlags[i])
        : returns;

    // If no active days, return zero metrics
    if (activeReturns.length === 0) {
        return {
            total_return: 1, // 1 means 0% return
            volatility: 0,
            conditional_sharpe_ratio: 0,
            conditional_value_at_risk: 0,
            max_drawdown: 0,
            sharpe_ratio: 0,
            omega_ratio: 0,
            sortino_ratio: 0,
            gain_loss_ratio: 0,
            upside_potential_ratio: 0
        };
    }

    const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
    const std = (arr: number[], ddof = 0) => {
        const avg = mean(arr);
        const variance = arr.reduce((sum, r) => sum + Math.pow(r - avg, 2), 0) / (arr.length - ddof);
        return Math.sqrt(variance);
    };

    // Helper functions remain the same
    const lpm = (returns: number[], threshold: number, order: number) => {
        const diff = returns.map(r => Math.max(0, threshold - r));
        return diff.reduce((sum, d) => sum + Math.pow(d, order), 0) / Math.max(1, returns.length);
    };

    const hpm = (returns: number[], threshold: number, order: number) => {
        const diff = returns.map(r => Math.max(0, r - threshold));
        return diff.reduce((sum, d) => sum + Math.pow(d, order), 0) / Math.max(1, returns.length);
    };

    // Calculate total return only for active days
    const totalReturn = activeReturns.reduce((acc, r) => acc * (1 + r), 1);
    const volatility = std(activeReturns);

    const maxDrawdown = (() => {
        if (activeReturns.length <= 1) return 0.0;
        const values = activeReturns.reduce((acc, r) => {
            acc.push(acc[acc.length - 1] * (1 + r));
            return acc;
        }, [1]);

        let maxDD = 0;
        let peak = values[0];

        for (let i = 1; i < values.length; i++) {
            if (values[i] > peak) {
                peak = values[i];
            }
            const drawdown = (peak - values[i]) / peak;
            maxDD = Math.max(maxDD, drawdown);
        }

        return maxDD;
    })();

    const cvar = (() => {
        const sorted = [...activeReturns].sort((a, b) => a - b);
        const alpha = 0.05;
        const idx = Math.floor(alpha * sorted.length);

        if (idx === 0 || sorted.length === 0) return 0;

        const tail = sorted.slice(0, idx);
        const result = Math.abs(tail.reduce((a, b) => a + b, 0) / idx);
        return isFinite(result) ? result : 0;
    })();

    const er = mean(activeReturns);
    const excessReturn = er - riskFreeRate;

    const downsideDeviation = Math.sqrt(lpm(activeReturns, 0, 2));
    const lpm1 = lpm(activeReturns, riskFreeRate, 1);
    const hpm1 = hpm(activeReturns, riskFreeRate, 1);

    const gainLossRatio = (() => {
        const gains = activeReturns.filter(r => r > 0).reduce((sum, r) => sum + r, 0);
        const losses = activeReturns.filter(r => r < 0).reduce((sum, r) => sum + Math.abs(r), 0);
        return safe(gains / Math.max(1e-6, losses));
    })();

    return {
        total_return: totalReturn,
        volatility: safe(volatility),
        conditional_sharpe_ratio: safe(cvar > 0 ? excessReturn / cvar : 0),
        conditional_value_at_risk: cvar,
        max_drawdown: maxDrawdown,
        sharpe_ratio: safe(excessReturn / Math.max(1e-6, volatility)),
        omega_ratio: safe(hpm1 / Math.max(1e-6, lpm1)),
        sortino_ratio: safe(excessReturn / Math.max(1e-6, downsideDeviation)),
        gain_loss_ratio: gainLossRatio,
        upside_potential_ratio: safe(hpm(activeReturns, 0, 1) / Math.max(1e-6, Math.sqrt(lpm(activeReturns, 0, 2))))
    };
}

import { ClusterFeatureSeries, FeatureTimeseriesData } from "./types";

// Extract cluster features over time
export function computeClusterFeatures(
    timeseries: any[],
    clusterIds: number[],
    strategy: string
): ClusterFeatureSeries {
    const timeseriesData: FeatureTimeseriesData[] = [];
    const allFeatureNamesSet = new Set<string>();
    let asset = "";

    for (let i = 0; i < timeseries.length; i++) {
        const { timestamp, data } = timeseries[i];

        if (i === 0) {
            asset = data?.price?.symbol ?? "";
        }

        const rank = data?.cluster_id;
        const rankMap = data?.cluster_assignment[strategy] as Record<string, number> | undefined;
        const activeClusterId = rankMap && typeof rank === "number"
            ? rankMap[rank] ?? rankMap[rank.toString()]
            : undefined;

        const clusterFeatures: Record<number, Record<string, number>> = {};

        if (data?.cluster_feature_means && rankMap) {
            Object.entries(data.cluster_feature_means).forEach(([originalClusterId, features]) => {
                const originalId = parseInt(originalClusterId);
                const mappedClusterId = rankMap[originalId] ?? rankMap[originalId.toString()];

                if (mappedClusterId !== undefined &&
                    !isNaN(mappedClusterId) &&
                    clusterIds.includes(mappedClusterId)) {
                    const featureObj = features as Record<string, number>;
                    clusterFeatures[mappedClusterId] = featureObj;

                    // Collect all feature names
                    Object.keys(featureObj).forEach(featureName => {
                        if (featureName !== "cluster_id") {
                            allFeatureNamesSet.add(featureName);
                        }
                    });
                }
            });
        }

        timeseriesData.push({
            timestamp,
            clusterFeatures,
            activeClusterId: activeClusterId !== undefined && activeClusterId !== -1 ? activeClusterId : undefined
        });
    }

    return {
        asset,
        timeseriesData,
        allFeatureNames: Array.from(allFeatureNamesSet).sort()
    };
}

// Types for metric rules
export interface MetricRule {
    id: string;
    field: string;
    operator: "gt" | "lt" | "gte" | "lte" | "eq" | "neq";
    threshold: number;
    allocationFactor: number;
    enabled: boolean;
}

export interface RuleGroup {
    id: string;
    rules: MetricRule[];
    logic: "AND" | "OR";
}

export interface MetricRuleSet {
    groups: RuleGroup[];
    groupLogic: "AND" | "OR";
}

// Helper to extract all available fields from data
export const extractAvailableFields = (timeseriesData: any[]): string[] => {
    if (timeseriesData.length === 0) return [];

    const lastData = timeseriesData[timeseriesData.length - 1]?.data;
    if (!lastData) return [];

    const fields = new Set<string>();


    // Add cluster-specific fields from clusters map
    if (lastData.clusters) {
        Object.values(lastData.clusters).forEach((cluster: any) => {
            if (cluster) {
                Object.keys(cluster).forEach(key => fields.add(`metrics.${key}`));
            }
        });
    }

    // Add feature_means fields
    if (lastData.cluster_feature_means) {
        Object.keys(lastData.cluster_feature_means).forEach(clusterId => {
            if (lastData.cluster_feature_means[clusterId]) {
                Object.keys(lastData.cluster_feature_means[clusterId]).forEach(key =>
                    fields.add(`features.${key}`)
                );
            }
        });
    }


    return Array.from(fields).sort();
};

const getFieldValue = (
    clusterFeatures: Record<number, Record<string, number>>,
    clusterMetrics: Record<number, Record<string, number>>,
    field: string,
    clusterId: number
): number | null => {
    const [category, key] = field.split(".");

    const sourceMap =
        category === "features" ? clusterFeatures :
            category === "metrics" ? clusterMetrics :
                null;

    return sourceMap?.[clusterId]?.[key] ?? null;
};


// Helper to evaluate a single rule
const evaluateRule = (
    rule: MetricRule,
    clusterFeatures: Record<number, Record<string, number>>,
    clusterMetrics: Record<number, Record<string, number>>,
    clusterId: number
): boolean => {
    const value = getFieldValue(clusterFeatures, clusterMetrics, rule.field, clusterId);
    if (value === null) return false;

    if (!rule.enabled) return true;

    switch (rule.operator) {
        case "gt":
            return value > rule.threshold;
        case "lt":
            return value < rule.threshold;
        case "gte":
            return value >= rule.threshold;
        case "lte":
            return value <= rule.threshold;
        case "eq":
            return value === rule.threshold;
        case "neq":
            return value !== rule.threshold;
        default:
            return false;
    }
};

interface RuleEvaluationResult {
    applied: boolean;
    factor: number;
}

const evaluateRuleSet = (
    ruleSet: MetricRuleSet,
    clusterFeatures: Record<number, Record<string, number>>,
    clusterMetrics: Record<number, Record<string, number>>,
    clusterId: number
): RuleEvaluationResult => {
    if (ruleSet.groups.length === 0) {
        return { applied: false, factor: 1 };
    }

    const groupResults = ruleSet.groups.map(group => {
        if (group.rules.length === 0) {
            return { applied: false, factor: 1 };
        }

        const ruleResults = group.rules.map(rule => {
            const passed = evaluateRule(rule, clusterFeatures, clusterMetrics, clusterId);
            return {
                passed,
                factor: rule.allocationFactor,
                enabled: rule.enabled !== false
            };
        });

        if (group.logic === "AND") {
            const allPassed = ruleResults.every(r => r.passed);
            const allEnabled = ruleResults.every(r => r.enabled);

            if (allPassed) {
                const minFactor = Math.min(...ruleResults.map(r => r.factor));
                return { applied: allEnabled, factor: minFactor };
            } else {
                const minFactor = Math.min(...ruleResults.map(r => r.factor));
                return { applied: false, factor: minFactor };
            }
        } else { // OR logic
            const passedRule = ruleResults.find(r => r.passed);
            if (passedRule) {
                return { applied: passedRule.enabled, factor: passedRule.factor };
            } else {
                return { applied: false, factor: ruleResults[0]?.factor ?? 1 };
            }
        }
    });

    if (ruleSet.groupLogic === "AND") {
        const allApplied = groupResults.every(result => result.applied);
        if (allApplied) {
            const minFactor = Math.min(...groupResults.map(r => r.factor));
            return { applied: true, factor: minFactor };
        } else {
            const minFactor = Math.min(...groupResults.map(r => r.factor));
            return { applied: false, factor: minFactor };
        }
    } else { // OR logic
        const appliedGroup = groupResults.find(result => result.applied);
        if (appliedGroup) {
            return { applied: true, factor: appliedGroup.factor };
        } else {
            const firstFactor = groupResults[0]?.factor ?? 1;
            return { applied: false, factor: firstFactor };
        }
    }
};

// Add to the end of calculation.ts

export interface AssetMetrics {
    asset: string;
    profitLoss: number;
    profitLossPercent: number;
    timeHeld: number; // in days
    totalReturn: number;
    sharpeRatio: number;
    maxDrawdown: number;
    volatility: number;
    averageReturn: number;
    winRate: number;
}

export function calculateAssetMetrics(
    timeseriesData: StrategyReturnSeries["timeseriesData"],
    rawTimeseries: any[],
    riskFreeRate = 0
): { totalMetrics: Omit<AssetMetrics, "asset">, assetMetrics: AssetMetrics[] } {
    const assetContributions: Record<string, {
        returns: number[];
        contributions: number[];
        allocations: number[];
        timeHeldMs: number;
        prices: number[];
        timestamps: string[];
    }> = {};

    // Initialize tracking
    for (const data of timeseriesData) {
        for (const asset of Object.keys(data.prices)) {
            if (!assetContributions[asset]) {
                assetContributions[asset] = {
                    returns: [],
                    contributions: [],
                    allocations: [],
                    timeHeldMs: 0,
                    prices: [],
                    timestamps: []
                };
            }
        }
    }

    // === NEW: track active periods ===
    const activeReturns: number[] = [];
    let activeTimeMs = 0;

    // Collect asset-level returns and contributions
    for (let i = 1; i < timeseriesData.length; i++) {
        const current = timeseriesData[i];
        const previous = timeseriesData[i - 1];
        const prevDist = rawTimeseries[i - 1]?.data?.target_distribution || {};
        const timeDiff = new Date(current.timestamp).getTime() - new Date(previous.timestamp).getTime();

        // Determine if portfolio was active (any allocation > 0)
        const isActive = Object.values(prevDist).some(v => parseFloat(v as any) > 0);

        if (isActive) {
            activeReturns.push(current.strategyReturn);
            activeTimeMs += timeDiff;
        }

        for (const asset of Object.keys(current.prices)) {
            const currentPrice = current.prices[asset];
            const prevPrice = previous.prices[asset];
            const allocation = parseFloat(prevDist[asset] || 0);


            if (allocation > 0 && prevPrice > 0 && currentPrice > 0) {
                const assetReturn = (currentPrice - prevPrice) / prevPrice;
                const contribution = allocation * assetReturn;

                const rec = assetContributions[asset];
                rec.returns.push(assetReturn);
                rec.contributions.push(contribution);
                rec.allocations.push(allocation);
                rec.timeHeldMs += timeDiff;
                rec.prices.push(currentPrice);
                rec.timestamps.push(current.timestamp);
            }
        }
    }

    // === Compute Asset Metrics ===
    const assetMetrics: AssetMetrics[] = Object.entries(assetContributions)
        .filter(([_, data]) => data.returns.length > 0)
        .map(([asset, data]) => {
            const returns = data.returns;
            const contributions = data.contributions;
            const timeHeld = data.timeHeldMs / (1000 * 60 * 60 * 24);

            // 1 Raw asset cumulative return (price-only)
            const cumulativeAssetReturn = returns.reduce((acc, r) => acc * (1 + r), 1) - 1;

            // 2 Portfolio-weighted compounded return for this asset
            //    This mimics the compounded effect the asset had on portfolio P/L
            let compoundedContribution = 1;
            for (const c of contributions) compoundedContribution *= (1 + c);
            const weightedCumulativeReturn = compoundedContribution - 1;

            // 3 Risk metrics on asset returns
            const metrics = calculateStrategyMetrics(returns, riskFreeRate);

            // 4 Win rate & average return
            const positiveCount = returns.filter(r => r > 0).length;
            const winRate = returns.length ? positiveCount / returns.length : 0;
            const averageReturn = returns.length
                ? returns.reduce((a, b) => a + b, 0) / returns.length
                : 0;

            // 5 Max drawdown
            const values = [1];
            for (const r of returns) values.push(values.at(-1)! * (1 + r));
            let peak = values[0];
            let maxDD = 0;
            for (const v of values) {
                if (v > peak) peak = v;
                const dd = (peak - v) / peak;
                if (dd > maxDD) maxDD = dd;
            }

            if (asset == 'trx') {
                console.log("trx")
                console.log(returns)
            }

            if (asset == 'algo') {
                console.log("algo")
                console.log(returns)
            }

            return {
                asset,
                profitLoss: weightedCumulativeReturn,
                profitLossPercent: weightedCumulativeReturn,
                totalReturn: weightedCumulativeReturn,
                timeHeld,
                sharpeRatio: metrics.sharpe_ratio,
                maxDrawdown: maxDD,
                volatility: metrics.volatility,
                averageReturn,
                winRate
            };
        })
        .sort((a, b) => Math.abs(b.totalReturn) - Math.abs(a.totalReturn));

    // === Total strategy metrics ===
    const allReturns = timeseriesData.slice(1).map(d => d.strategyReturn);
    const totalMetricsCalc = calculateStrategyMetrics(allReturns, riskFreeRate);
    const cumulativeStrategyReturn = allReturns.reduce((acc, r) => acc * (1 + r), 1) - 1;
    const avgStrategyReturn = allReturns.reduce((a, b) => a + b, 0) / allReturns.length;
    // === NEW: compute active-based win rate & timeHeld ===
    const activePositive = activeReturns.filter(r => r > 0).length;
    const activeWinRate = activeReturns.length ? activePositive / activeReturns.length : 0;
    const activeTimeDays = activeTimeMs / (1000 * 60 * 60 * 24);

    const totalMetrics = {
        profitLoss: cumulativeStrategyReturn,
        profitLossPercent: cumulativeStrategyReturn,
        totalReturn: cumulativeStrategyReturn,
        sharpeRatio: totalMetricsCalc.sharpe_ratio,
        maxDrawdown: totalMetricsCalc.max_drawdown,
        volatility: totalMetricsCalc.volatility,
        averageReturn: avgStrategyReturn,
        timeHeld: activeTimeDays,        // only when portfolio invested
        winRate: activeWinRate           // only based on active days
    };

    return { totalMetrics, assetMetrics };
}


/**
 * Calculates benchmark metrics from a simple candle timeseries.
 * Expects data points like [{ timestamp, close }]
 */
export function calculateBenchmarkMetrics(
    benchmarkTimeseriesData: StrategyReturnSeries["timeseriesData"],
    riskFreeRate = 0,
): Omit<AssetMetrics, "asset"> {
    if (!benchmarkTimeseriesData || benchmarkTimeseriesData.length < 2) {
        return {
            profitLoss: 0,
            profitLossPercent: 0,
            totalReturn: 0,
            sharpeRatio: 0,
            maxDrawdown: 0,
            volatility: 0,
            averageReturn: 0,
            winRate: 0,
            timeHeld: 0
        };
    }

    console.log(benchmarkTimeseriesData)

    // Calculate simple returns between consecutive closes
    const returns: number[] = [];
    for (let i = 1; i < benchmarkTimeseriesData.length; i++) {
        returns.push(benchmarkTimeseriesData[i].strategyReturn);
    }

    // Compound cumulative return
    const cumulativeReturn = returns.reduce((acc, r) => acc * (1 + r), 1) - 1;

    // Risk metrics
    const metrics = calculateStrategyMetrics(returns, riskFreeRate);

    // Win rate and average return
    const positiveReturns = returns.filter(r => r > 0).length;
    const winRate = returns.length > 0 ? positiveReturns / returns.length : 0;
    const averageReturn = returns.length > 0
        ? returns.reduce((a, b) => a + b, 0) / returns.length
        : 0;

    // Max drawdown (rebuild cumulative series)
    const values = [1];
    for (const r of returns) values.push(values.at(-1)! * (1 + r));
    let peak = values[0];
    let maxDD = 0;
    for (const v of values) {
        if (v > peak) peak = v;
        const dd = (peak - v) / peak;
        if (dd > maxDD) maxDD = dd;
    }

    // Time held (days between first and last candle)
    const firstTs = new Date(benchmarkTimeseriesData[0].timestamp).getTime();
    const lastTs = new Date(benchmarkTimeseriesData.at(-1)!.timestamp).getTime();

    return {
        profitLoss: cumulativeReturn,
        profitLossPercent: cumulativeReturn,
        totalReturn: cumulativeReturn,
        sharpeRatio: metrics.sharpe_ratio,
        maxDrawdown: maxDD,
        volatility: metrics.volatility,
        averageReturn,
        winRate,
        timeHeld: benchmarkTimeseriesData.length
    };
}
